import {
  type PayloadAction,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { current } from 'immer';
import { compact, get, isEmpty, keyBy, uniq } from 'lodash';
import { startAppListening } from 'slices/listenerMiddleware';
import type { TAppThunk, TRootState } from 'slices/store';
import { createAppAsyncThunk } from 'slices/utils';
import type { IDefaultReducerState } from 'types/state.interface';
import {
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
  prepEntityForAPICall,
} from 'utils/sliceHelpers';
import {
  type TOrganizationNode,
  type TOrganizationTreeNode,
  type TOrganizationTreeReturn,
  createOrganizationTreeNode,
  createOrganizationNode as createTOrganizationNode,
} from '@timeedit/types';
import api from '../services/api.service';

export const NEW_ORG_NODE_ID = 'new_node';

export type TOrganizationHierarchyState = IDefaultReducerState &
  TOrganizationTreeReturn & {
    firstDrawer: TOrganizationNodeDrawer | null;
    secondDrawer: TOrganizationNodeDrawer | null;
    selectedOrganizationNodeIds: string[];
    expandedTreeNodes: string[];
    tree: TOrganizationTreeNode;
    map: Record<string, TOrganizationNode>;
    treeMap: TTreeMap;
    // List of leaf nodes, including ids and names
    leafNodes: Array<string>;
  };

export type TOrganizationNodeDrawer = TLocalOrganizationNode & {
  idTable?: string;
};

export type TLocalOrganizationNode = TOrganizationNode & {
  virtualId?: string;
  description?: string;
  isEdited?: boolean;
};

export type TMappedOrganizationTreeNode = {
  title: string;
  key: string;
  value: string;
  id: string;
  parentId?: string;
  children?: TMappedOrganizationTreeNode[];
};

type TTreeMap = {
  ancestorsByName: Record<string, Pick<TOrganizationTreeNode, 'name'>[]>;
  byVirtualId: Record<string, string>;
  virtualIdsById: Record<string, string[]>;
};

export const initialState: TOrganizationHierarchyState = {
  loading: false,
  hasErrors: false,
  firstDrawer: null,
  secondDrawer: null,
  expandedTreeNodes: [],
  leafNodes: [],
  map: {},
  nodes: [],
  selectedOrganizationNodeIds: [],
  tree: {
    id: 'root',
    name: 'root',
    virtualId: 'root',
    children: [],
  },
  treeMap: {
    ancestorsByName: {},
    byVirtualId: {},
    virtualIdsById: {},
  },
};

// Slice
const slice = createSlice({
  name: 'organizationHierarchy',
  initialState,
  reducers: {
    organizationHierarchyReducerRequest: (state) => {
      isLoadingRequest(state);
    },
    organizationHierarchyReducerSuccess: (
      state,
      { payload }: PayloadAction<TOrganizationNodesResponse>
    ) => {
      const { tree, nodes } = payload;
      const nodeTree = nodes.map((node) => createTOrganizationNode(node));
      state.tree = createOrganizationTreeNode(tree);

      const { leafNodes, treeMap } = initializeNodeRelationData(state.tree);
      state.leafNodes = leafNodes;
      state.treeMap = treeMap;

      state.nodes = nodeTree;
      state.map = keyBy(nodeTree, 'id');
      finishedLoadingSuccess(state);
    },
    organizationHierarchyReducerFailure: (state) => {
      finishedLoadingFailure(state);
    },
    updateDrawer: (
      state,
      {
        payload,
      }: PayloadAction<{
        isFirstDrawer: boolean;
        data: TOrganizationNodeDrawer | null;
      }>
    ) => {
      const { isFirstDrawer, data } = payload;
      if (isFirstDrawer) {
        state.firstDrawer = data;
      } else {
        state.secondDrawer = data;
      }
    },
    addNewNodeLocal: (
      state,
      {
        payload: { newNode, parentTreeNode },
      }: PayloadAction<{
        newNode: TLocalOrganizationNode;
        parentTreeNode: TParentTreeNode;
      }>
    ) => {
      state.map[newNode.id] = newNode;
      state.firstDrawer = newNode;
      updateChildren([state.tree], parentTreeNode, newNode);
    },
    deleteNodeLocal: (state) => {
      state.map[NEW_ORG_NODE_ID] = undefined;
      deleteChildren([state.tree]);
    },

    onDiscardForm: (
      state,
      {
        payload,
      }: PayloadAction<{
        isFirstDrawer: boolean;
        id: string;
      }>
    ) => {
      const { isFirstDrawer, id } = payload;
      if (isFirstDrawer) {
        state.firstDrawer = state.map[id];
      } else {
        state.secondDrawer = state.map[id];
      }
    },
    onRowClick: (
      state,
      { payload }: PayloadAction<{ record: TMappedOrganizationTreeNode }>
    ) => {
      const { record } = payload;
      const firstDrawer = state?.firstDrawer
        ? current(state?.firstDrawer)
        : null;
      const nodesMap = current(state.map);
      if (firstDrawer && get(firstDrawer, 'key') === record.key) {
        state.firstDrawer = null;
      } else {
        if (record.key) {
          state.firstDrawer = { ...nodesMap[record.value], idTable: record.id };
        }
      }
    },
    handleCompare: (
      state,
      {
        payload,
      }: PayloadAction<{
        record: TMappedOrganizationTreeNode;
      }>
    ) => {
      const { record } = payload;
      const firstDrawer = state?.firstDrawer
        ? current(state?.firstDrawer)
        : null;
      const nodesMap = current(state.map);
      if (firstDrawer) {
        state.secondDrawer = { ...nodesMap[record.value], idTable: record.id };
      } else {
        state.firstDrawer = { ...nodesMap[record.value], idTable: record.id };
      }
    },
    setExpandedTreeNodes: (state, { payload }: PayloadAction<string[]>) => {
      state.expandedTreeNodes = payload;
    },
    setSelectedOrganizationNodeIds: (
      state,
      { payload }: PayloadAction<string[]>
    ) => {
      state.selectedOrganizationNodeIds = payload;
    },
  },
  selectors: {
    selectedOrganizationNodeIdsSelector: (state) =>
      state.selectedOrganizationNodeIds,
  },
});

export default slice.reducer;

function initializeNodeRelationData(
  node: TOrganizationTreeNode,
  treeMap: TTreeMap = {
    ancestorsByName: {},
    byVirtualId: {},
    virtualIdsById: {},
  },
  leafNodeSet = new Set<string>()
) {
  treeMap.byVirtualId[node.virtualId] = node.id;
  treeMap.virtualIdsById[node.id] = [
    ...(treeMap.virtualIdsById[node.id] || []),
    node.virtualId,
  ];

  for (const child of node.children ?? []) {
    treeMap.ancestorsByName[child.name] = (
      treeMap.ancestorsByName[node.name] ?? []
    ).concat({
      name: node.name,
    });
    initializeNodeRelationData(child, treeMap, leafNodeSet);
  }

  if (isEmpty(node.children)) {
    leafNodeSet.add(node.id).add(node.name);
  }

  return { leafNodes: Array.from(leafNodeSet), treeMap };
}

export const organizationHierarchyLoading = (state: TRootState) =>
  state.organizationHierarchy.loading;
export const organizationTreeSelector = (state: TRootState) =>
  state.organizationHierarchy.tree;
export const organizationTreeByVirtualIdSelector = (state: TRootState) =>
  state.organizationHierarchy.treeMap.byVirtualId;
export const organizationNodesSelector = (state: TRootState) =>
  state.organizationHierarchy.nodes;
export const organizationMapSelector = (state: TRootState) =>
  state.organizationHierarchy.map;
export const selectOrganizationNodeLeaves = createSelector(
  (state: TRootState) => state.organizationHierarchy,
  (organizationHierarchy) => {
    /**
     * Two definitions of leaves:
     * - All tree nodes without children
     * - All nodes with no other node having it as a parent
     * This function implements the second version
     */
    const nodes = organizationHierarchy.nodes || [];
    // Get a unique list of all node ids that are parents
    const allParentIds = uniq(
      nodes.reduce((allKeys: string[], node: TOrganizationNode) => {
        allKeys.push(...node.parentNodeIds);
        return allKeys;
      }, [])
    );
    // Filter through all nodes, excluding items which are in the list of parentIds
    return nodes.filter((node) => !allParentIds.includes(node.id || ''));
  }
);

export const selectFirstDrawer = (state: TRootState) =>
  state.organizationHierarchy.firstDrawer;
export const selectSecondDrawer = (state: TRootState) =>
  state.organizationHierarchy.secondDrawer;
export const selectExpandedTreeNodes = (state: TRootState) =>
  state.organizationHierarchy.expandedTreeNodes;
export const selectLeafNodes = (state: TRootState) =>
  state.organizationHierarchy.leafNodes;
export const selectTreeMap = (state: TRootState) =>
  state.organizationHierarchy.treeMap;

export const fetchOrganizationHierarchy = createAppAsyncThunk(
  'organizationHierarchy/fetchOrganizationHierarchy',
  async (_, { dispatch }) => {
    try {
      dispatch(organizationHierarchyReducerRequest());
      const organizationHierarchy = (await api.get({
        endpoint: `/organization-nodes`,
      })) as TOrganizationNodesResponse;
      dispatch(organizationHierarchyReducerSuccess(organizationHierarchy));
    } catch (e) {
      dispatch(organizationHierarchyReducerFailure());
      return console.error(e);
    }
  }
);

type TParentTreeNode = {
  title: string;
  key: string;
  value: string;
  id: string;
  children?: TParentTreeNode[];
  parentId?: string;
};

export const createOrganizationNodeLocal =
  (
    parentNode?: Partial<TOrganizationTreeNode>,
    parentTreeNode?: TParentTreeNode
  ): TAppThunk =>
  (dispatch) => {
    const newNode: TLocalOrganizationNode = {
      id: NEW_ORG_NODE_ID,
      virtualId: NEW_ORG_NODE_ID,
      name: 'New node',
      description: '',
      parentNodeIds: parentNode?.id ? [parentNode?.id] : [],
      isEdited: true,
    };
    dispatch(
      addNewNodeLocal({
        newNode,
        parentTreeNode,
      })
    );
  };

export const createOrganization = createAppAsyncThunk<
  void,
  TOrganizationNodeDrawer
>('organizationHierarchy/createOrganization', async (newNode, { dispatch }) => {
  try {
    dispatch(organizationHierarchyReducerRequest());
    const organizationHierarchy = (await api.post({
      endpoint: `/organization-nodes`,
      data: {
        name: newNode.name,
        description: newNode.description,
        parentNodeIds: newNode.parentNodeIds,
        extId: newNode.extId,
      },
    })) as TOrganizationNodesResponse;
    dispatch(updateDrawer({ isFirstDrawer: true, data: null }));
    dispatch(organizationHierarchyReducerSuccess(organizationHierarchy));
  } catch (e) {
    dispatch(organizationHierarchyReducerFailure());
    return console.error(e);
  }
});

type TSaveOrganizationNodePayload = {
  organizationNode: TOrganizationNode;
  isFirstDrawer?: boolean;
};

type TOrganizationNodesResponse = {
  nodes: TOrganizationNodeResponseNode[];
  tree: TOrganizationTreeNode[];
};

type TOrganizationNodeResponseNode = {
  id: string;
  name: string;
  parentNodeIds?: string[];
  legacyId: number;
  isLeaf: boolean;
  description?: string;
  extId?: string;
};

export const saveOrganizationNode = createAppAsyncThunk<
  void,
  TSaveOrganizationNodePayload
>(
  'organizationHierarchy/saveOrganizationNode',
  async ({ isFirstDrawer, organizationNode }, { dispatch }) => {
    try {
      dispatch(organizationHierarchyReducerRequest());
      /**
       * This will always be a PATCH, since - unlike a lot of the other resources -
       * nodes are created and saved on the server side before being returned to the client
       */
      const preppedOrganizationNode = prepEntityForAPICall(
        organizationNode,
        true,
        true
      );
      const organizationHierarchy = (await api.patch({
        endpoint: `/organization-nodes/${organizationNode.id}`,
        data: preppedOrganizationNode,
      })) as TOrganizationNodesResponse;
      dispatch(organizationHierarchyReducerSuccess(organizationHierarchy));
      if (typeof isFirstDrawer === 'boolean') {
        dispatch(
          updateDrawer({
            isFirstDrawer,
            data: {
              ...organizationNode,
              isEdited: false,
            },
          })
        );
      }
    } catch (error) {
      dispatch(organizationHierarchyReducerFailure());
      return console.error(error);
    }
  }
);

type TDeleteOrganizationNodePayload = {
  organizationNodeId: string;
  isFirstDrawer?: boolean;
};

export const deleteOrganizationNode = createAppAsyncThunk<
  void,
  TDeleteOrganizationNodePayload
>(
  'organizationHierarchy/deleteOrganizationNode',
  async ({ isFirstDrawer, organizationNodeId }, { dispatch }) => {
    try {
      dispatch(organizationHierarchyReducerRequest());
      const organizationHierarchy = (await api.delete({
        endpoint: `/organization-nodes/${organizationNodeId}`,
      })) as TOrganizationNodesResponse;
      dispatch(organizationHierarchyReducerSuccess(organizationHierarchy));

      if (isFirstDrawer !== undefined) {
        dispatch(updateDrawer({ isFirstDrawer, data: null }));
      }
    } catch (e) {
      dispatch(organizationHierarchyReducerFailure());
      console.error(e);
    }
  }
);

const updateChildren = (
  tree: TOrganizationTreeNode[] | undefined,
  parentTree: TParentTreeNode,
  newNode: TLocalOrganizationNode
): TOrganizationTreeNode | undefined => {
  if (!tree) {
    return;
  }

  return tree.find((item) => {
    if (item.virtualId === parentTree.key) {
      // @ts-expect-error FIXME state.tree has children, newNode does not
      item.children = [...(item.children || []), newNode];
      return true;
    } else {
      return updateChildren(item.children, parentTree, newNode);
    }
  });
};

const deleteChildren = (
  tree: TOrganizationTreeNode[]
): TOrganizationTreeNode | undefined => {
  return tree.find((item, index, array) => {
    if (item.virtualId === NEW_ORG_NODE_ID) {
      array.splice(index, 1);
      return true;
    } else {
      return deleteChildren(item.children);
    }
  });
};

export const {
  organizationHierarchyReducerRequest,
  organizationHierarchyReducerSuccess,
  organizationHierarchyReducerFailure,
  updateDrawer,
  addNewNodeLocal,
  deleteNodeLocal,
  onDiscardForm,
  onRowClick,
  handleCompare,
  setExpandedTreeNodes,
  setSelectedOrganizationNodeIds,
} = slice.actions;

export const { selectedOrganizationNodeIdsSelector } = slice.selectors;

startAppListening({
  predicate: (_, currentState, previousState) =>
    currentState.organizationHierarchy.firstDrawer !==
      previousState.organizationHierarchy.firstDrawer ||
    currentState.organizationHierarchy.secondDrawer !==
      previousState.organizationHierarchy.secondDrawer,
  effect: (_, { dispatch, getState }) => {
    const {
      organizationHierarchy: { firstDrawer, secondDrawer },
    } = getState();

    dispatch(
      setSelectedOrganizationNodeIds(
        compact([
          firstDrawer
            ? firstDrawer.idTable || `${firstDrawer.id ?? ''}#1`
            : null,
          secondDrawer ? secondDrawer.idTable : null,
        ])
      )
    );
  },
});

export const checkDrawerExist = (): TAppThunk<boolean> => (_, getState) =>
  !!getState().organizationHierarchy.firstDrawer ||
  !!getState().organizationHierarchy.secondDrawer;
